import { theme as antdTheme } from 'antd';
import type { ThemeConfig as AntdThemeConfig } from 'antd';
import type { ThemeConfig } from '../../types';

/** Fields that belong to our app theme, not Ant Design token */
const APP_THEME_KEYS = new Set([
    'isDark',
    'isCompact',
    'backgroundImage',
    'cardTransparency',
    'cardWidth',
    'regularCardTitles',
]);

/** Soft charcoal dark surfaces — not pure black */
export const DARK_SURFACE_DEFAULTS = {
    colorBgLayout: '#1a1a1a',
    colorBgContainer: '#2b2b2b',
    colorBgElevated: '#333333',
    // Borders must stay lighter than elevated, or card outlines disappear
    colorBorder: '#555555',
    colorBorderSecondary: '#4a4a4a',
    colorFillSecondary: 'rgba(255, 255, 255, 0.08)',
    colorFillTertiary: 'rgba(255, 255, 255, 0.06)',
    colorFillQuaternary: 'rgba(255, 255, 255, 0.04)',
};

/** Fixed tooltip chrome — never follows theme color pickers */
const TOOLTIP_BY_MODE = {
    light: {
        colorBgSpotlight: 'rgba(0, 0, 0, 0.85)',
        colorTextLightSolid: '#ffffff',
    },
    dark: {
        colorBgSpotlight: '#1f1f1f',
        colorTextLightSolid: '#ffffff',
    },
} as const;

function normalizeHex(color: string): string {
    return color.trim().toLowerCase();
}

function isNearBlack(color: string): boolean {
    const c = normalizeHex(color);
    return c === '#000' || c === '#000000' || c === 'black' || c === 'rgb(0, 0, 0)' || c === 'rgb(0,0,0)';
}

function isNearWhite(color: string): boolean {
    const c = normalizeHex(color);
    return c === '#fff' || c === '#ffffff' || c === 'white' || c === 'rgb(255, 255, 255)' || c === 'rgb(255,255,255)';
}

function parseRgb(color: string): { r: number; g: number; b: number } | null {
    const c = normalizeHex(color);
    if (c.startsWith('#')) {
        const hex = c.slice(1);
        if (hex.length === 3) {
            return {
                r: parseInt(hex[0] + hex[0], 16),
                g: parseInt(hex[1] + hex[1], 16),
                b: parseInt(hex[2] + hex[2], 16),
            };
        }
        if (hex.length === 6 && /^[0-9a-f]{6}$/.test(hex)) {
            return {
                r: parseInt(hex.slice(0, 2), 16),
                g: parseInt(hex.slice(2, 4), 16),
                b: parseInt(hex.slice(4, 6), 16),
            };
        }
        return null;
    }
    const rgb = c.match(/^rgba?\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)/);
    if (!rgb) {
        return null;
    }
    return { r: Number(rgb[1]), g: Number(rgb[2]), b: Number(rgb[3]) };
}

/** Light leftover surfaces from a previously-light theme JSON */
function isLightSurface(color: string): boolean {
    if (isNearWhite(color)) {
        return true;
    }
    const lum = luminance(color);
    return lum !== null && lum >= 0.55;
}

function luminance(color: string): number | null {
    const rgb = parseRgb(color);
    if (!rgb) {
        return null;
    }
    return (0.2126 * rgb.r + 0.7152 * rgb.g + 0.0722 * rgb.b) / 255;
}

/** Dark leftover text (#000, #0f172a) that would be invisible on dark surfaces */
function isDarkColor(color: string): boolean {
    if (isNearBlack(color)) {
        return true;
    }
    const lum = luminance(color);
    return lum !== null && lum < 0.45;
}

const DARK_TEXT_DEFAULTS = {
    colorText: 'rgba(255, 255, 255, 0.88)',
    colorTextHeading: 'rgba(255, 255, 255, 0.88)',
    colorTextLabel: 'rgba(255, 255, 255, 0.88)',
    colorTextDescription: 'rgba(255, 255, 255, 0.65)',
    colorTextSecondary: 'rgba(255, 255, 255, 0.65)',
    colorTextTertiary: 'rgba(255, 255, 255, 0.45)',
    colorTextQuaternary: 'rgba(255, 255, 255, 0.25)',
    colorIcon: 'rgba(255, 255, 255, 0.88)',
    colorIconHover: 'rgba(255, 255, 255, 0.88)',
};

/**
 * Build Ant Design theme from app ThemeConfig.
 * Dark mode: algorithm seeds stay #fff/#000; user "text" / "bg" pickers map to alias tokens.
 */
export function buildAntdTheme(theme: ThemeConfig | null | undefined): AntdThemeConfig {
    if (!theme) {
        return { cssVar: { key: 'vdelo' } };
    }

    const algorithm = [];
    if (theme.isDark) {
        algorithm.push(antdTheme.darkAlgorithm);
    }
    if (theme.isCompact) {
        algorithm.push(antdTheme.compactAlgorithm);
    }

    const raw = theme as ThemeConfig & Record<string, unknown>;
    const token: Record<string, unknown> = {};

    for (const [key, value] of Object.entries(raw)) {
        if (APP_THEME_KEYS.has(key)) {
            continue;
        }
        if (value === undefined || value === null || value === '') {
            continue;
        }
        token[key] = value;
    }

    if (theme.isDark) {
        const pickedText = theme.colorTextBase;
        const pickedBg = theme.colorBgBase;

        // Required seeds for darkAlgorithm (do not use picker values as seeds)
        token.colorBgBase = '#fff';
        token.colorTextBase = '#000';

        // Soft dark defaults always (override stale light borders from old theme JSON)
        Object.assign(token, DARK_SURFACE_DEFAULTS);

        // Keep custom dark surfaces; drop leftover light #fff from old theme JSON
        if (
            raw.colorBgContainer &&
            !isNearBlack(String(raw.colorBgContainer)) &&
            !isLightSurface(String(raw.colorBgContainer))
        ) {
            token.colorBgContainer = raw.colorBgContainer;
        }
        if (
            raw.colorBgElevated &&
            !isNearBlack(String(raw.colorBgElevated)) &&
            !isLightSurface(String(raw.colorBgElevated))
        ) {
            token.colorBgElevated = raw.colorBgElevated;
        }
        if (raw.colorBgLayout && !isNearBlack(String(raw.colorBgLayout)) && !isNearWhite(String(raw.colorBgLayout))) {
            token.colorBgLayout = raw.colorBgLayout;
        }

        // Borders: soft gray. Drop leftover light borders (#f0f0f0, #d9d9d9, #fff)
        token.colorBorder = DARK_SURFACE_DEFAULTS.colorBorder;
        token.colorBorderSecondary = DARK_SURFACE_DEFAULTS.colorBorderSecondary;
        const elevated = normalizeHex(String(token.colorBgElevated));
        const container = normalizeHex(String(token.colorBgContainer));
        if (
            raw.colorBorder &&
            !isNearWhite(String(raw.colorBorder)) &&
            !isLightSurface(String(raw.colorBorder)) &&
            normalizeHex(String(raw.colorBorder)) !== elevated &&
            normalizeHex(String(raw.colorBorder)) !== container
        ) {
            token.colorBorder = raw.colorBorder;
        }
        if (
            raw.colorBorderSecondary &&
            !isNearWhite(String(raw.colorBorderSecondary)) &&
            !isLightSurface(String(raw.colorBorderSecondary)) &&
            normalizeHex(String(raw.colorBorderSecondary)) !== elevated &&
            normalizeHex(String(raw.colorBorderSecondary)) !== container
        ) {
            token.colorBorderSecondary = raw.colorBorderSecondary;
        }

        Object.assign(token, DARK_TEXT_DEFAULTS);

        // Picker → visible light text. Dark leftovers (#000, #0f172a) stay on the defaults.
        if (pickedText && !isDarkColor(pickedText)) {
            token.colorText = pickedText;
            token.colorTextHeading = pickedText;
            token.colorTextLabel = pickedText;
            token.colorTextDescription = pickedText;
            token.colorTextSecondary = pickedText;
            token.colorTextTertiary = pickedText;
            token.colorTextQuaternary = pickedText;
            token.colorIcon = pickedText;
            token.colorIconHover = pickedText;
        }

        if (pickedBg && !isNearWhite(pickedBg) && !isNearBlack(pickedBg)) {
            token.colorBgLayout = pickedBg;
        } else if (pickedBg && isNearBlack(pickedBg)) {
            token.colorBgLayout = DARK_SURFACE_DEFAULTS.colorBgLayout;
        }
    } else {
        token.colorBgBase = theme.colorBgBase || '#fff';
        token.colorTextBase = theme.colorTextBase || '#000';
    }

    const tooltip = theme.isDark ? TOOLTIP_BY_MODE.dark : TOOLTIP_BY_MODE.light;

    // Иконки не должны расти/сжиматься вместе с размером шрифта темы
    token.fontSizeIcon = 16;

    return {
        token,
        algorithm: algorithm.length ? algorithm : undefined,
        cssVar: { key: 'vdelo' },
        components: {
            Tooltip: {
                colorBgSpotlight: tooltip.colorBgSpotlight,
                colorTextLightSolid: tooltip.colorTextLightSolid,
            },
            // У icon-only кнопок по умолчанию onlyIconSize: inherit → тянет fontSize темы
            Button: {
                onlyIconSize: 16,
                onlyIconSizeSM: 16,
                onlyIconSizeLG: 16,
            },
        },
    };
}
